/* eslint-disable prefer-const */
import { useEffect, useState } from "react";
import LinksAndMedia from "./LinksAndMedia";
import GroupHeader from "./GroupHeader";
import GroupMembers from "./GroupMembers";
import GroupActions from "./GroupActions";
import StarMessageTab from "./StarMessageTab";
import GroupNameCard from "./GroupNameCard";
import { IoClose } from "react-icons/io5";
import { toggleProfileView } from "../../../store/Slices/ViewManagerSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import TextTranslate from "../../../utils/TextTranslate";
import AboutConversation from "./AboutConversation";
import useApiPost from "../../../hooks/PostData";
import { UpdateGroupRes } from "../../../types/ResType";
import { updateCurrentConversation } from "../../../store/Slices/CurrentConversationSlice";
import { updateByChatId } from "../../../store/Slices/ChatListSlice";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import { useConversationMembers } from "../../../store/api/useConversationMembers";

export default function GroupInfo() {
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const userData = useAppSelector((state) => state.userData);
  const { data: GroupMemberData } = useConversationMembers();
  let dispatch = useAppDispatch();
  // @ts-ignore
  const { theme } = useTheme();
  const { loading, postData } = useApiPost();
  const isGroup = currentConversationData.chat_type == "group";
  const isGroupAdmin = !!GroupMemberData?.data?.Records?.find(
    (row) => row.User?.user_id === userData.user_id,
  )?.is_admin;
  const [groupNameDraft, setGroupNameDraft] = useState(
    currentConversationData.group_name || "",
  );
  const [nameDirty, setNameDirty] = useState(false);

  useEffect(() => {
    setGroupNameDraft(currentConversationData.group_name || "");
    setNameDirty(false);
  }, [currentConversationData.chat_id, currentConversationData.group_name]);

  async function saveGroupName() {
    if (!isGroupAdmin) return;
    const next = groupNameDraft.trim();
    if (!next || next === (currentConversationData.group_name || "").trim()) {
      setNameDirty(false);
      setGroupNameDraft(currentConversationData.group_name || "");
      return;
    }

    const updateGroupProfile: UpdateGroupRes = await postData(
      "chat/update-group",
      {
        chat_id: currentConversationData.chat_id,
        group_name: next,
      },
      "multipart/form-data",
    );

    if (updateGroupProfile.status == true) {
      toast.success("Group Name Updated", { position: "top-right" });
      dispatch(
        updateCurrentConversation({
          group_name: updateGroupProfile.data.group_name,
        }),
      );
      dispatch(
        updateByChatId({
          chat_id: currentConversationData.chat_id,
          group_name: updateGroupProfile.data.group_name,
        }),
      );
      setNameDirty(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-secondary">
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-black/[0.06] bg-secondary/85 px-3 py-3 backdrop-blur-xl dark:border-white/[0.08]">
        <button
          type="button"
          aria-label="Close"
          className="grid h-9 w-9 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10"
          onClick={() => dispatch(toggleProfileView(false))}
        >
          <IoClose className="text-xl" />
        </button>
        <span className="text-[17px] font-semibold tracking-tight text-darkText">
          <TextTranslate
            text={isGroup ? "Group Info" : "Contact Info"}
          />
        </span>
        {isGroup && isGroupAdmin && nameDirty ? (
          <button
            type="button"
            disabled={loading}
            onClick={() => void saveGroupName()}
            className="min-w-9 px-1 text-[15px] font-semibold text-themeColor disabled:opacity-50"
          >
            {loading ? (
              <ClipLoader
                size={16}
                color={theme == "dark" ? "white" : "black"}
              />
            ) : (
              <TextTranslate text="Update" />
            )}
          </button>
        ) : (
          <div className="h-9 w-9" aria-hidden />
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-10">
        <GroupHeader />

        <div className="space-y-3 px-4">
          {isGroup && isGroupAdmin && (
            <GroupNameCard
              value={groupNameDraft}
              onChange={(next) => {
                setGroupNameDraft(next);
                setNameDirty(
                  next.trim() !==
                    (currentConversationData.group_name || "").trim(),
                );
              }}
            />
          )}
          <AboutConversation />
          <LinksAndMedia />
          <StarMessageTab />
          <GroupMembers />
          <GroupActions />
        </div>
      </div>
    </div>
  );
}
